import { Context, Data, Schema } from 'effect';
import type { Effect } from 'effect';

import type { CountryT } from '@vs-shop/schemas/enum/country';
import type { LocaleT } from '@vs-shop/schemas/tenant/locale';
import type { PacketaKey } from '../../config/packeta';
import { ErrorTag } from '../../data/error';
import { ContextTag } from '../context';
import { PickupCarrier, type PickupPoint } from '../shipping/PickupPoint';
import { decodePlacementId } from '../shipping/PlacementId';

export type PacketaT = {
  readonly id: string;
  readonly name: string;
  readonly city: string;
  readonly street: string;
  readonly zip: string;
};

/**
 * What the widget's callback hands over for an internal pickup point.
 *
 * The carrier documents `id` for its own points; a point of an external
 * carrier arrives with `carrierId`/`carrierPickupPointId` instead, which this
 * storefront does not sell. `id` accepts both number and string because the
 * docs and the live widget disagree on the representation.
 */
export const PacketaSelectionSchema = Schema.Struct({
  id: Schema.Union([Schema.NonEmptyString, Schema.Finite]),
  name: Schema.String,
  city: Schema.String,
  street: Schema.String,
  zip: Schema.String,
});

export type PacketaSelection = Schema.Schema.Type<typeof PacketaSelectionSchema>;

export const toPacketaT = (selection: PacketaSelection): PacketaT => ({
  id: String(selection.id),
  name: selection.name,
  city: selection.city,
  street: selection.street,
  zip: selection.zip,
});

export const PacketaWidgetLayout = {
  PUDO: 'pudo',
} as const;

/**
 * Packeta identifies a point by its own id, so that is what the backend gets.
 *
 * The widget also reports `carrierId` and `carrierPickupPointId` for points
 * operated by an external carrier. Only `id` is read, which is the identifier
 * for Packeta's own pick-up points — the only kind this checkout offers.
 *
 * @see https://docs.packeta.com/docs/pudo-delivery/widget
 */
export const toPickupPoint = (point: PacketaT): PickupPoint => ({
  carrier: PickupCarrier.Packeta,
  placementId: decodePlacementId(point.id),
  name: point.name,
  street: point.street,
  city: point.city,
  zip: point.zip,
});

export class PacketaWidgetNotLoadedError extends Data.TaggedError(
  ErrorTag.PacketaWidgetNotLoadedError
)<{
  message: string;
}> {}

export class PacketaWidgetCancelledError extends Data.TaggedError(
  ErrorTag.PacketaWidgetCancelledError
)<{
  message: string;
}> {}

export class PacketaWidgetScriptLoadError extends Data.TaggedError(
  ErrorTag.PacketaWidgetScriptLoadError
)<{
  message: string;
  cause?: unknown;
}> {}

export class PacketaWidgetPayloadError extends Data.TaggedError(
  ErrorTag.PacketaWidgetPayloadError
)<{
  message: string;
  cause?: unknown;
}> {}

export type PacketaWidgetOptions = {
  readonly language: LocaleT;
  readonly country: CountryT;
  readonly layout?: string;
  readonly inlineContainerId?: string;
};

export type PacketaWidgetService = {
  readonly loadScript: Effect.Effect<void, PacketaWidgetScriptLoadError>;
  readonly pick: (
    apiKey: PacketaKey,
    options: PacketaWidgetOptions
  ) => Effect.Effect<
    PacketaT,
    | PacketaWidgetNotLoadedError
    | PacketaWidgetCancelledError
    | PacketaWidgetScriptLoadError
    | PacketaWidgetPayloadError
  >;
  readonly close: Effect.Effect<void>;
};

export class PacketaWidget extends Context.Service<PacketaWidget, PacketaWidgetService>()(
  ContextTag.PacketaWidget
) {}
